import React, { ChangeEvent, useRef, useEffect } from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { DISABLE_RTL_STYLES, DISABLE_RTL_PROPS } from "../../../../utils/rtlUtils";
import { COMMON_MUI_STYLES } from "../../../../styles";

interface PasswordFieldProps {
  name: string;
  placeholder: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  autoComplete?: string;
  /** When true, browser autofill is not synced into React state (use on signup) */
  disableAutofillSync?: boolean;
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
  name,
  placeholder,
  value,
  onChange,
  showPassword,
  onTogglePassword,
  error,
  helperText,
  disabled,
  autoComplete,
  disableAutofillSync = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Handle auto-fill: ensure type is controlled even after browser auto-fill
  // iOS Safari needs more aggressive handling
  useEffect(() => {
    const input = inputRef.current?.querySelector('input');
    if (input) {
      // Force the correct type after auto-fill
      const checkType = () => {
        const expectedType = showPassword ? "text" : "password";
        if (input.type !== expectedType) {
          // For iOS, we need to be more aggressive
          if (isIOS) {
            // Store current value and selection
            const currentValue = input.value;
            const selectionStart = input.selectionStart;
            const selectionEnd = input.selectionEnd;
            
            // Force type change
            input.type = expectedType;
            
            // Restore value and selection (iOS sometimes clears it)
            if (input.value !== currentValue) {
              input.value = currentValue;
            }
            
            // Restore selection if it was set
            if (selectionStart !== null && selectionEnd !== null) {
              setTimeout(() => {
                input.setSelectionRange(selectionStart, selectionEnd);
              }, 0);
            }
          } else {
            input.type = expectedType;
          }
        }
      };

      // Check immediately and after delays (auto-fill happens asynchronously)
      checkType();
      const timeoutId1 = setTimeout(checkType, 100);
      const timeoutId2 = setTimeout(checkType, 300);
      const timeoutId3 = setTimeout(checkType, 500);
      
      // Listen for various events that might indicate auto-fill
      input.addEventListener('input', checkType);
      input.addEventListener('change', checkType);
      input.addEventListener('blur', checkType);
      
      // For iOS, also use MutationObserver to watch for type changes
      let observer: MutationObserver | null = null;
      if (isIOS) {
        observer = new MutationObserver(checkType);
        observer.observe(input, {
          attributes: true,
          attributeFilter: ['type']
        });
      }
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        input.removeEventListener('input', checkType);
        input.removeEventListener('change', checkType);
        input.removeEventListener('blur', checkType);
        observer?.disconnect();
      };
    }
  }, [showPassword, isIOS]);

  // Handle auto-fill by syncing value on input event
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (disableAutofillSync) {
      return;
    }
    if (e.target.value !== value) {
      onChange(e);
    }
  };

  return (
    <TextField
      ref={inputRef}
      {...DISABLE_RTL_PROPS}
      margin="normal"
      fullWidth
      id={name}
      placeholder={placeholder}
      name={name}
      type={showPassword ? "text" : "password"}
      autoComplete={autoComplete || (name === "password" ? "current-password" : "new-password")}
      value={value}
      onChange={onChange}
      onInput={handleInput}
      error={error}
      helperText={helperText}
      disabled={disabled}
      InputProps={{
        endAdornment: (
          <InputAdornment 
            position="end"
            {...DISABLE_RTL_PROPS}
          >
            <IconButton
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTogglePassword();
                
                // For iOS, force type change immediately on toggle click (handles iPhone password generator)
                if (isIOS) {
                  setTimeout(() => {
                    const input = inputRef.current?.querySelector('input');
                    if (input) {
                      const expectedType = !showPassword ? "text" : "password";
                      const currentValue = input.value;
                      input.type = expectedType;
                      if (input.value !== currentValue) {
                        input.value = currentValue;
                      }
                    }
                  }, 0);
                }
              }}
              edge="end"
              tabIndex={-1}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        // Apply RTL-disabling styles first, then common text field styles
        // so that shared color tokens (including placeholder color) win.
        ...DISABLE_RTL_STYLES,
        ...COMMON_MUI_STYLES.textField,
        // Ensure placeholder styling matches email field while keeping LTR alignment.
        "& input::placeholder": {
          textAlign: "left !important",
          color: "var(--color-text-primary)",
          fontWeight: 500,
          opacity: 1,
        },
      }}
    />
  );
};
